import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { 
  Plus, BookOpen, Clock, Play, CheckCircle, XCircle, 
  AlertTriangle, FileText, Award, Users, Settings, Eye, Send, Timer
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_TYPES = [
  { value: 'mcq', label: 'Multiple Choice' },
  { value: 'true_false', label: 'True/False' },
  { value: 'fill_blank', label: 'Fill in Blank' }
];

const DIFFICULTY = [
  { value: 'easy', label: 'Easy', color: 'bg-green-100 text-green-800' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-800' },
  { value: 'hard', label: 'Hard', color: 'bg-red-100 text-red-800' }
];

const OnlineExamPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [activeTab, setActiveTab] = useState('exams');
  const [loading, setLoading] = useState(false);
  
  // Data
  const [exams, setExams] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [myResults, setMyResults] = useState([]);
  
  // Exam Taking State
  const [currentExam, setCurrentExam] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [examResult, setExamResult] = useState(null);
  
  // Dialogs
  const [questionDialog, setQuestionDialog] = useState(false);
  const [examDialog, setExamDialog] = useState(false);
  const [addQuestionsDialog, setAddQuestionsDialog] = useState(false);
  const [examTakingDialog, setExamTakingDialog] = useState(false);
  const [resultDialog, setResultDialog] = useState(false);
  const [examResultsDialog, setExamResultsDialog] = useState(false);
  const [examResults, setExamResults] = useState([]);
  
  // Forms
  const [questionForm, setQuestionForm] = useState({
    subject_id: '', class_id: '', question_text: '', question_type: 'mcq',
    options: [{ id: '1', text: '', is_correct: false }, { id: '2', text: '', is_correct: false },
              { id: '3', text: '', is_correct: false }, { id: '4', text: '', is_correct: false }],
    correct_answer: '', marks: 1, difficulty: 'medium', explanation: ''
  });
  const [examForm, setExamForm] = useState({
    title: '', description: '', subject_id: '', class_ids: [], duration_minutes: 60,
    pass_marks: 40, start_time: '', end_time: '', shuffle_questions: true,
    show_result_immediately: true, negative_marking: false
  });
  const [selectedExamForQuestions, setSelectedExamForQuestions] = useState(null);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user?.role === 'student') {
      fetchMyResults();
    }
  }, [user]);

  // Timer
  useEffect(() => {
    let timer;
    if (examTakingDialog && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmitExam(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [examTakingDialog, timeRemaining]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [examsRes, questionsRes, subjectsRes, classesRes] = await Promise.all([
        axios.get(`${API_URL}/api/online-exam/exams`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/online-exam/questions`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/timetable/subjects`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() })
      ]);
      setExams(examsRes.data);
      setQuestions(questionsRes.data);
      setSubjects(subjectsRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      console.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyResults = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/online-exam/my-results`, { headers: getAuthHeader() });
      setMyResults(res.data);
    } catch (error) {
      console.error('Failed to fetch results');
    }
  };

  const createQuestion = async () => {
    try {
      await axios.post(`${API_URL}/api/online-exam/questions`, questionForm, { headers: getAuthHeader() });
      toast.success('Question created!');
      setQuestionDialog(false);
      resetQuestionForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to create question');
    }
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      subject_id: '', class_id: '', question_text: '', question_type: 'mcq',
      options: [{ id: '1', text: '', is_correct: false }, { id: '2', text: '', is_correct: false },
                { id: '3', text: '', is_correct: false }, { id: '4', text: '', is_correct: false }],
      correct_answer: '', marks: 1, difficulty: 'medium', explanation: ''
    });
  };

  const createExam = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/online-exam/exams`, examForm, { headers: getAuthHeader() });
      toast.success('Exam created!');
      setExamDialog(false);
      setExamForm({ title: '', description: '', subject_id: '', class_ids: [], duration_minutes: 60, pass_marks: 40, start_time: '', end_time: '', shuffle_questions: true, show_result_immediately: true, negative_marking: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to create exam');
    }
  };

  const addQuestionsToExam = async () => {
    if (!selectedExamForQuestions || selectedQuestionIds.length === 0) return;
    try {
      await axios.post(`${API_URL}/api/online-exam/exams/${selectedExamForQuestions.id}/questions`, selectedQuestionIds, { headers: getAuthHeader() });
      toast.success('Questions added!');
      setAddQuestionsDialog(false);
      setSelectedQuestionIds([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to add questions');
    }
  };

  const updateExamStatus = async (examId, status) => {
    try {
      await axios.patch(`${API_URL}/api/online-exam/exams/${examId}/status?status=${status}`, {}, { headers: getAuthHeader() });
      toast.success(`Exam ${status}!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const startExam = async (exam) => {
    try {
      const res = await axios.post(`${API_URL}/api/online-exam/exams/${exam.id}/start`, {}, { headers: getAuthHeader() });
      setCurrentExam(res.data.exam);
      setAttemptId(res.data.attempt_id);
      setExamQuestions(res.data.questions);
      setTimeRemaining(res.data.time_remaining_seconds);
      setAnswers(res.data.saved_answers || {});
      setCurrentQuestionIndex(0);
      setExamTakingDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start exam');
    }
  };

  const saveAnswer = async (questionId, optionId, textAnswer) => {
    setAnswers(prev => ({ ...prev, [questionId]: { selected_option_id: optionId, text_answer: textAnswer } }));
    try {
      await axios.post(`${API_URL}/api/online-exam/exams/save-answer?attempt_id=${attemptId}&question_id=${questionId}${optionId ? `&selected_option_id=${optionId}` : ''}${textAnswer ? `&text_answer=${encodeURIComponent(textAnswer)}` : ''}`, {}, { headers: getAuthHeader() });
    } catch (error) {
      console.error('Auto-save failed');
    }
  };

  const handleSubmitExam = async (timedOut = false) => {
    try {
      const res = await axios.post(`${API_URL}/api/online-exam/exams/${attemptId}/submit`, {}, { headers: getAuthHeader() });
      setExamTakingDialog(false);
      setExamResult(res.data);
      setResultDialog(true);
      fetchMyResults();
    } catch (error) {
      toast.error('Failed to submit exam');
    }
  };

  const viewExamResults = async (examId) => {
    try {
      const res = await axios.get(`${API_URL}/api/online-exam/exams/${examId}/results`, { headers: getAuthHeader() });
      setExamResults(res.data);
      setExamResultsDialog(true);
    } catch (error) {
      console.error('Failed to load results:', error);
      toast.error(error.response?.data?.detail || 'Failed to load results');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isAdmin = user?.role === 'school_admin' || user?.role === 'super_admin';
  const isTeacher = user?.role === 'teacher';
  const isStudent = user?.role === 'student';

  return (
    <DashboardLayout title="Online Exam">
      <div className="space-y-6" data-testid="online-exam-page">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="exams" data-testid="exams-tab"><BookOpen className="h-4 w-4 mr-1" /> Exams</TabsTrigger>
            {(isAdmin || isTeacher) && <TabsTrigger value="questions" data-testid="questions-tab"><FileText className="h-4 w-4 mr-1" /> Question Bank</TabsTrigger>}
            {isStudent && <TabsTrigger value="results" data-testid="my-results-tab"><Award className="h-4 w-4 mr-1" /> My Results</TabsTrigger>}
          </TabsList>

          {/* Exams Tab */}
          <TabsContent value="exams" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{isStudent ? 'Available Exams' : 'Online Exams'}</h2>
              {(isAdmin || isTeacher) && (
                <Button onClick={() => setExamDialog(true)} data-testid="create-exam-btn">
                  <Plus className="h-4 w-4 mr-1" /> Create Exam
                </Button>
              )}
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {exams.map(exam => (
                <Card key={exam.id} className="hover:shadow-md transition-shadow" data-testid={`exam-card-${exam.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{exam.title}</CardTitle>
                        <CardDescription>{exam.subject_name} • {exam.duration_minutes} mins</CardDescription>
                      </div>
                      <Badge className={exam.status === 'published' ? 'bg-green-100 text-green-800' : exam.status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'}>
                        {exam.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-muted-foreground mb-3">
                      <p><Clock className="inline h-4 w-4 mr-1" /> {exam.start_time?.slice(0, 16)} - {exam.end_time?.slice(0, 16)}</p>
                      <p><FileText className="inline h-4 w-4 mr-1" /> {exam.question_count || 0} Questions • {exam.total_marks} Marks</p>
                    </div>
                    <div className="flex gap-2">
                      {isStudent && (exam.status === 'published' || exam.status === 'active') && (
                        <Button size="sm" onClick={() => startExam(exam)} data-testid={`start-exam-${exam.id}`}>
                          <Play className="h-4 w-4 mr-1" /> Start Exam
                        </Button>
                      )}
                      {(isAdmin || isTeacher) && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedExamForQuestions(exam); setAddQuestionsDialog(true); }}>
                            <Plus className="h-4 w-4 mr-1" /> Questions
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => viewExamResults(exam.id)}>
                            <Users className="h-4 w-4 mr-1" /> Results
                          </Button>
                          {exam.status === 'draft' && (
                            <Button size="sm" onClick={() => updateExamStatus(exam.id, 'published')}>Publish</Button>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {exams.length === 0 && (
                <p className="col-span-3 text-center text-muted-foreground py-8">No exams available</p>
              )}
            </div>
          </TabsContent>

          {/* Question Bank Tab */}
          <TabsContent value="questions" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Question Bank ({questions.length})</h2>
              <Button onClick={() => setQuestionDialog(true)} data-testid="add-question-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Question
              </Button>
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full" data-testid="questions-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-3 text-left text-sm">Question</th>
                    <th className="p-3 text-left text-sm">Subject</th>
                    <th className="p-3 text-center text-sm">Type</th>
                    <th className="p-3 text-center text-sm">Marks</th>
                    <th className="p-3 text-center text-sm">Difficulty</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.slice(0, 20).map(q => (
                    <tr key={q.id} className="border-t">
                      <td className="p-3 max-w-md truncate">{q.question_text}</td>
                      <td className="p-3 text-sm">{q.subject_name}</td>
                      <td className="p-3 text-center"><Badge variant="outline">{q.question_type}</Badge></td>
                      <td className="p-3 text-center">{q.marks}</td>
                      <td className="p-3 text-center">
                        <Badge className={DIFFICULTY.find(d => d.value === q.difficulty)?.color}>{q.difficulty}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {questions.length === 0 && <p className="text-center text-muted-foreground py-8">No questions in bank</p>}
            </div>
          </TabsContent>

          {/* My Results Tab (Student) */}
          <TabsContent value="results" className="space-y-4">
            <h2 className="text-lg font-semibold">My Exam Results</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myResults.map(r => (
                <Card key={r.attempt_id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{r.exam_title}</CardTitle>
                    <CardDescription>{new Date(r.submitted_at).toLocaleDateString()}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl font-bold">{r.obtained_marks}/{r.total_marks}</span>
                      <Badge className={r.is_passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {r.is_passed ? 'PASS' : 'FAIL'}
                      </Badge>
                    </div>
                    <Progress value={r.percentage} className="mb-2" />
                    <p className="text-sm text-muted-foreground">{r.percentage}%</p>
                  </CardContent>
                </Card>
              ))}
              {myResults.length === 0 && <p className="col-span-3 text-center text-muted-foreground py-8">No exam results yet</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Question Dialog */}
      <Dialog open={questionDialog} onOpenChange={setQuestionDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Question</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Subject *</Label>
                <Select value={questionForm.subject_id} onValueChange={v => setQuestionForm(p => ({ ...p, subject_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select Subject" /></SelectTrigger>
                  <SelectContent>
                    {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Question Type</Label>
                <Select value={questionForm.question_type} onValueChange={v => setQuestionForm(p => ({ ...p, question_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Question *</Label>
              <Textarea value={questionForm.question_text} onChange={e => setQuestionForm(p => ({ ...p, question_text: e.target.value }))} placeholder="Enter your question..." rows={3} data-testid="question-text-input" />
            </div>
            
            {(questionForm.question_type === 'mcq' || questionForm.question_type === 'true_false') && (
              <div>
                <Label>Options (Select correct answer)</Label>
                <div className="space-y-2 mt-2">
                  {(questionForm.question_type === 'true_false' 
                    ? [{ id: '1', text: 'True', is_correct: false }, { id: '2', text: 'False', is_correct: false }]
                    : questionForm.options
                  ).map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Checkbox
                        checked={opt.is_correct}
                        onCheckedChange={(c) => {
                          const opts = questionForm.question_type === 'true_false'
                            ? [{ id: '1', text: 'True', is_correct: idx === 0 && c }, { id: '2', text: 'False', is_correct: idx === 1 && c }]
                            : questionForm.options.map((o, i) => ({ ...o, is_correct: i === idx ? c : false }));
                          setQuestionForm(p => ({ ...p, options: opts }));
                        }}
                      />
                      {questionForm.question_type === 'mcq' ? (
                        <Input
                          value={opt.text}
                          onChange={e => {
                            const opts = [...questionForm.options];
                            opts[idx].text = e.target.value;
                            setQuestionForm(p => ({ ...p, options: opts }));
                          }}
                          placeholder={`Option ${idx + 1}`}
                        />
                      ) : (
                        <span className="font-medium">{opt.text}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {questionForm.question_type === 'fill_blank' && (
              <div>
                <Label>Correct Answer *</Label>
                <Input value={questionForm.correct_answer} onChange={e => setQuestionForm(p => ({ ...p, correct_answer: e.target.value }))} placeholder="Enter correct answer" />
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Marks</Label>
                <Input type="number" value={questionForm.marks} onChange={e => setQuestionForm(p => ({ ...p, marks: parseInt(e.target.value) }))} />
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={questionForm.difficulty} onValueChange={v => setQuestionForm(p => ({ ...p, difficulty: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DIFFICULTY.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Class (Optional)</Label>
                <Select value={questionForm.class_id} onValueChange={v => setQuestionForm(p => ({ ...p, class_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Explanation (Optional)</Label>
              <Textarea value={questionForm.explanation} onChange={e => setQuestionForm(p => ({ ...p, explanation: e.target.value }))} placeholder="Explain the correct answer..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuestionDialog(false)}>Cancel</Button>
            <Button onClick={createQuestion} data-testid="save-question-btn">Save Question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Exam Dialog */}
      <Dialog open={examDialog} onOpenChange={setExamDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Online Exam</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={examForm.title} onChange={e => setExamForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g., Math Quiz 1" data-testid="exam-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Subject *</Label>
                <Select value={examForm.subject_id} onValueChange={v => setExamForm(p => ({ ...p, subject_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (mins)</Label>
                <Input type="number" value={examForm.duration_minutes} onChange={e => setExamForm(p => ({ ...p, duration_minutes: parseInt(e.target.value) }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time *</Label>
                <Input type="datetime-local" value={examForm.start_time} onChange={e => setExamForm(p => ({ ...p, start_time: e.target.value }))} />
              </div>
              <div>
                <Label>End Time *</Label>
                <Input type="datetime-local" value={examForm.end_time} onChange={e => setExamForm(p => ({ ...p, end_time: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Classes *</Label>
              <div className="grid grid-cols-3 gap-2 mt-2 max-h-24 overflow-y-auto border rounded p-2">
                {classes.map(c => (
                  <div key={c.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={examForm.class_ids.includes(c.id)}
                      onCheckedChange={(checked) => {
                        if (checked) setExamForm(p => ({ ...p, class_ids: [...p.class_ids, c.id] }));
                        else setExamForm(p => ({ ...p, class_ids: p.class_ids.filter(id => id !== c.id) }));
                      }}
                    />
                    <span className="text-sm">{c.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pass Marks (%)</Label>
                <Input type="number" value={examForm.pass_marks} onChange={e => setExamForm(p => ({ ...p, pass_marks: parseInt(e.target.value) }))} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Checkbox checked={examForm.shuffle_questions} onCheckedChange={c => setExamForm(p => ({ ...p, shuffle_questions: c }))} />
                <Label>Shuffle Questions</Label>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Checkbox checked={examForm.show_result_immediately} onCheckedChange={c => setExamForm(p => ({ ...p, show_result_immediately: c }))} />
                <Label>Show Result Immediately</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={examForm.negative_marking} onCheckedChange={c => setExamForm(p => ({ ...p, negative_marking: c }))} />
                <Label>Negative Marking</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExamDialog(false)}>Cancel</Button>
            <Button onClick={createExam} data-testid="create-exam-submit">Create Exam</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Questions to Exam Dialog */}
      <Dialog open={addQuestionsDialog} onOpenChange={setAddQuestionsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Add Questions to: {selectedExamForQuestions?.title}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="p-2 text-left"><Checkbox checked={selectedQuestionIds.length === questions.length} onCheckedChange={(c) => setSelectedQuestionIds(c ? questions.map(q => q.id) : [])} /></th>
                  <th className="p-2 text-left text-sm">Question</th>
                  <th className="p-2 text-center text-sm">Type</th>
                  <th className="p-2 text-center text-sm">Marks</th>
                </tr>
              </thead>
              <tbody>
                {questions.map(q => (
                  <tr key={q.id} className="border-t">
                    <td className="p-2">
                      <Checkbox
                        checked={selectedQuestionIds.includes(q.id)}
                        onCheckedChange={(c) => {
                          if (c) setSelectedQuestionIds(p => [...p, q.id]);
                          else setSelectedQuestionIds(p => p.filter(id => id !== q.id));
                        }}
                      />
                    </td>
                    <td className="p-2 max-w-md truncate">{q.question_text}</td>
                    <td className="p-2 text-center"><Badge variant="outline">{q.question_type}</Badge></td>
                    <td className="p-2 text-center">{q.marks}</td>
                  </tr>
                ))}
                {questions.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-4 text-center text-muted-foreground">
                      No questions available. Please add questions to the Question Bank first.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <span className="text-sm text-muted-foreground mr-auto">{selectedQuestionIds.length} selected</span>
            <Button variant="outline" onClick={() => setAddQuestionsDialog(false)}>Cancel</Button>
            <Button onClick={addQuestionsToExam}>Add Selected</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exam Taking Dialog */}
      <Dialog open={examTakingDialog} onOpenChange={() => {}}>
        <DialogContent className="max-w-4xl max-h-[90vh]" hideClose>
          {currentExam && examQuestions.length > 0 && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>{currentExam.title}</DialogTitle>
                  <div className={`flex items-center gap-2 text-lg font-mono ${timeRemaining < 300 ? 'text-red-600' : ''}`}>
                    <Timer className="h-5 w-5" />
                    {formatTime(timeRemaining)}
                  </div>
                </div>
                <Progress value={(currentQuestionIndex + 1) / examQuestions.length * 100} className="mt-2" />
                <p className="text-sm text-muted-foreground">Question {currentQuestionIndex + 1} of {examQuestions.length}</p>
              </DialogHeader>
              
              <div className="py-4">
                <div className="bg-muted/30 p-4 rounded-lg mb-4">
                  <p className="text-lg font-medium">{examQuestions[currentQuestionIndex].question_text}</p>
                  <Badge variant="outline" className="mt-2">{examQuestions[currentQuestionIndex].marks} Mark(s)</Badge>
                </div>
                
                {examQuestions[currentQuestionIndex].question_type === 'fill_blank' ? (
                  <Input
                    value={answers[examQuestions[currentQuestionIndex].id]?.text_answer || ''}
                    onChange={e => saveAnswer(examQuestions[currentQuestionIndex].id, null, e.target.value)}
                    placeholder="Type your answer..."
                    className="text-lg"
                  />
                ) : (
                  <RadioGroup
                    value={answers[examQuestions[currentQuestionIndex].id]?.selected_option_id || ''}
                    onValueChange={v => saveAnswer(examQuestions[currentQuestionIndex].id, v, null)}
                    className="space-y-3"
                  >
                    {examQuestions[currentQuestionIndex].options.map((opt, idx) => (
                      <div key={opt.id} className={`flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer ${answers[examQuestions[currentQuestionIndex].id]?.selected_option_id === opt.id ? 'border-primary bg-primary/5' : ''}`}>
                        <RadioGroupItem value={opt.id} id={opt.id} />
                        <Label htmlFor={opt.id} className="flex-1 cursor-pointer">{String.fromCharCode(65 + idx)}. {opt.text}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              </div>
              
              {/* Question Navigator */}
              <div className="flex flex-wrap gap-2 border-t pt-4">
                {examQuestions.map((q, idx) => (
                  <button
                    key={q.id}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    className={`w-8 h-8 rounded text-sm font-medium ${
                      idx === currentQuestionIndex ? 'bg-primary text-white' :
                      answers[q.id] ? 'bg-green-100 text-green-800' : 'bg-gray-100'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
              
              <DialogFooter className="mt-4">
                <Button variant="outline" disabled={currentQuestionIndex === 0} onClick={() => setCurrentQuestionIndex(p => p - 1)}>Previous</Button>
                {currentQuestionIndex < examQuestions.length - 1 ? (
                  <Button onClick={() => setCurrentQuestionIndex(p => p + 1)}>Next</Button>
                ) : (
                  <Button onClick={() => handleSubmitExam()} className="bg-green-600 hover:bg-green-700" data-testid="submit-exam-btn">
                    <Send className="h-4 w-4 mr-1" /> Submit Exam
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={resultDialog} onOpenChange={setResultDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-center">Exam Result</DialogTitle>
          </DialogHeader>
          {examResult && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${examResult.result?.is_passed ? 'bg-green-100' : 'bg-red-100'} mb-4`}>
                  {examResult.result?.is_passed ? (
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  ) : (
                    <XCircle className="h-12 w-12 text-red-600" />
                  )}
                </div>
                <h3 className={`text-3xl font-bold ${examResult.result?.is_passed ? 'text-green-600' : 'text-red-600'}`}>
                  {examResult.result?.is_passed ? 'PASSED!' : 'FAILED'}
                </h3>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{examResult.result?.obtained_marks}/{examResult.result?.total_marks}</p>
                  <p className="text-xs text-muted-foreground">Marks</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{examResult.result?.percentage}%</p>
                  <p className="text-xs text-muted-foreground">Percentage</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{examResult.result?.correct_count}/{examResult.result?.total_questions}</p>
                  <p className="text-xs text-muted-foreground">Correct</p>
                </div>
              </div>
              
              <div className="flex justify-between text-sm bg-muted/50 p-3 rounded">
                <span>✓ Correct: {examResult.result?.correct_count}</span>
                <span>✗ Wrong: {examResult.result?.wrong_count}</span>
                <span>○ Skipped: {examResult.result?.unanswered_count}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResultDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exam Results Dialog (Admin) */}
      <Dialog open={examResultsDialog} onOpenChange={setExamResultsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Exam Results</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="p-2 text-left text-sm">Rank</th>
                  <th className="p-2 text-left text-sm">Student</th>
                  <th className="p-2 text-center text-sm">Marks</th>
                  <th className="p-2 text-center text-sm">%</th>
                  <th className="p-2 text-center text-sm">Correct</th>
                  <th className="p-2 text-center text-sm">Result</th>
                </tr>
              </thead>
              <tbody>
                {examResults.map(r => (
                  <tr key={r.attempt_id} className="border-t">
                    <td className="p-2 font-bold">#{r.rank}</td>
                    <td className="p-2">{r.student_name}</td>
                    <td className="p-2 text-center">{r.obtained_marks}/{r.total_marks}</td>
                    <td className="p-2 text-center">{r.percentage}%</td>
                    <td className="p-2 text-center">{r.correct_count}</td>
                    <td className="p-2 text-center">
                      <Badge className={r.is_passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {r.is_passed ? 'PASS' : 'FAIL'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExamResultsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default OnlineExamPage;
